import { array as YupArray, date as YupDate, object as YupObject, string as YupString } from 'yup';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { encodeDate, encodeTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { Box, Grid, Typography, TextField, Collapse } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { Select } from '@campfire/select';
import { DatePickerField } from '@campfire/date-picker';
import { Field } from '@campfire/field';
import { Room, Edit } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { Pane, PaneWrapper } from '@campfire/pane';
import { Calendar, DateObject } from 'react-multi-date-picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { arrayHead } from '../../../../common/functions/array-head';
import { AutoLocationField } from '../../../../common/auto-location-field/AutoLocationField';
import { useAddFlexibleSessionInstanceFetch } from '../main-section/roster/flexible-session-functions';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { getDisplayTimeSchedule } from '../../../../common/functions/activity-display-helpers';
import { ConfirmationDialog } from '../../../../common/dialogs/ConfirmationDialog';
import { useUser } from '../../../../global/auth/useUser';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useRosterTemplateActions, useSaveRosterTemplate } from './roster-template-actions';
import { RosterCancellationDialog } from './RosterCancellationDialog';
import { RosterNotesDialog } from '../common/RosterNotesDialog';
import {
  CREATE_ROSTER_TEMPLATE_SCREEN_GET_ACTIVITIES,
  CREATE_ROSTER_TEMPLATE_SCREEN_GET_PROGRAMS_AND_ACTIVITIES,
  ROSTER_TEMPLATE_SCREEN_CHECK_RECURRENCE_DATE,
  ROSTER_TEMPLATE_SCREEN_GET_ACTIVITY_OCCURRENCES,
} from './roster-template-model.gql';
import { CreateRosterTemplateScreenActivity } from './__generated__/CreateRosterTemplateScreenActivity';
import { CreateRosterTemplateScreenProgram } from './__generated__/CreateRosterTemplateScreenProgram';
import { CreateRosterTemplateScreenGetProgramsAndActivities } from './__generated__/CreateRosterTemplateScreenGetProgramsAndActivities';
import {
  RosterTemplateScreenCheckRecurrenceDate,
  RosterTemplateScreenCheckRecurrenceDateVariables,
} from './__generated__/RosterTemplateScreenCheckRecurrenceDate';
import {
  CreateRosterTemplateScreenGetActivities,
  CreateRosterTemplateScreenGetActivitiesVariables,
} from './__generated__/CreateRosterTemplateScreenGetActivities';
import { CreateRosterTemplateScreenActivitySession } from './__generated__/CreateRosterTemplateScreenActivitySession';
import {
  RosterTemplateScreenGetActivityOccurrences,
  RosterTemplateScreenGetActivityOccurrencesVariables,
} from './__generated__/RosterTemplateScreenGetActivityOccurrences';

const validationSchema = YupObject().shape({
  programId: YupString().required('Please select a program'),
  activityId: YupString().required('Please select an activity'),
  description: YupString(),
  rosterDates: YupArray()
    .of(YupDate())
    .min(1, 'Please select at least one date'),
  location: YupObject().required('Please provide a location'),
});

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '24px 16px',
    [theme.breakpoints.up('md')]: {
      padding: '24px 128px',
    },
    height: '100%',
    overflow: 'scroll',
  },
  textGap: {
    marginRight: 16,
    [theme.breakpoints.up('sm')]: {
      marginRight: 8,
    },
  },
}));

interface Location {
  description: string;
  formatted: string;
  latitude: number;
  longitude: number;
  placesId?: string;
}

export interface FlexibleSessionConfig {
  sessionId: string;
  customName: string;
  startTime: string;
  endTime: string;
}

export interface PositionalSessionConfig {
  index: number;
  customName: string;
  startTime: string;
  endTime: string;
}

export type RosterTemplateFormValues = {
  rosterTemplateId?: string;
  programId: string;
  activityId: string;
  rosterName?: string | null;
  rosterNotes?: string | null;
  rosterDates: Date[];
  location: Location;
  remoteLocation: any;
  flexibleSessionConfigs?: FlexibleSessionConfig[];
  positionalSessionConfigs?: PositionalSessionConfig[];
};

type DateMode = 'single' | 'bulk' | 'range';

const CreateRosterTemplateForm = ({
  activities,
  programs,
  loading,
  isSubmitting,
  publishedRoster,
  initRosterDates,
  onSuccess,
  onDeleted,
  inDialog,
  enableFlexibleSessions,
  onCreated,
  initialFlexibleSessionConfigs,
  positionalSessionConfigs,
}: {
  activities: CreateRosterTemplateScreenActivity[];
  programs: CreateRosterTemplateScreenProgram[];
  loading: boolean;
  isSubmitting: boolean;
  publishedRoster?: PublishedRoster | null;
  initRosterDates?: Date[];
  onSuccess?: (values?: RosterTemplateFormValues) => void;
  onDeleted?: () => void;
  inDialog?: boolean;
  enableFlexibleSessions?: boolean;
  onCreated?: (data: { rosterTemplateIds: string[]; activityId: string }) => void;
  initialFlexibleSessionConfigs?: FlexibleSessionConfig[];
  positionalSessionConfigs?: PositionalSessionConfig[];
}) => {
  const formik = useFormikContext<RosterTemplateFormValues>();
  const classes = useStyles();
  const history = useHistory();
  const { theme } = useCampfireTheme();
  const { runDeleteRosterTemplate, runSaveRosterTemplate } = useRosterTemplateActions();
  const saveRosterTemplateApi = useSaveRosterTemplate();
  const { setSnackbar } = useSnackbar();
  const [urlActivityId, setUrlActivityId] = useQueryParam('activityId', StringParam);
  const [localActivityId, setLocalActivityId] = useState<string | null | undefined>(
    formik.values.activityId || undefined
  );
  const selectedActivityId = inDialog ? localActivityId : urlActivityId;
  const setSelectedActivityId = (activityId: string | null | undefined) =>
    inDialog ? setLocalActivityId(activityId) : setUrlActivityId(activityId);
  const [view] = useQueryParam('view', StringParam);
  const [selectedProgramId, setSelectedProgramId] = useState<string>(formik.values.programId);
  const [dateMode, setDateMode] = useState<DateMode>('single');
  const [rangeStartDate, setRangeStartDate] = useState<Date | null>(null);
  const [rangeEndDate, setRangeEndDate] = useState<Date | null>(null);
  const [prevDate, setPrevDate] = useState(formik.values.rosterDates[0]);
  const queriedDateRef = useRef<string | null>(null);
  const [prevLocation] = useState(formik.values.location);
  const [showRosterExistDialog, setShowRosterExistDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [showRosterCancellationDialog, setShowRosterCancellationDialog] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showNoteSection, setShowNoteSection] = useState<boolean>(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [customizedSessions, setCustomizedSessions] = useState<Record<string, FlexibleSessionConfig>>(() => {
    // Initialize from initial configs if provided
    if (initialFlexibleSessionConfigs && initialFlexibleSessionConfigs.length > 0) {
      const initial: Record<string, FlexibleSessionConfig> = {};
      initialFlexibleSessionConfigs.forEach((config) => {
        initial[config.sessionId] = config;
      });
      return initial;
    }
    return {};
  });
  const addFlexibleSession = useAddFlexibleSessionInstanceFetch();

  const viewParam = view === 'bulk' ? '&view=bulk' : '';

  const isEditMode = useMemo(() => {
    return !!formik.values.rosterTemplateId;
  }, [formik.values.rosterTemplateId]);

  // Keep the selected activity id in sync with a prefilled/edited activity
  useEffect(() => {
    if (formik.values.activityId && formik.values.activityId !== selectedActivityId) {
      setSelectedActivityId(formik.values.activityId);
    }
  }, [formik.values.activityId]);

  const selectedActivity = useMemo(() => {
    return activities?.find((activity) => activity.activityId === selectedActivityId);
  }, [activities, selectedActivityId, isEditMode]);

  const sessionsOfSelectedActivity = useMemo(() => {
    return selectedActivity?.sessions ?? [];
  }, [selectedActivity]);

  // Apply positional session configs when activity changes and sessions are available
  // Track which activity we've applied configs to, to avoid re-applying
  const [appliedConfigsForActivity, setAppliedConfigsForActivity] = useState<string | null>(null);

  useEffect(() => {
    if (!positionalSessionConfigs || positionalSessionConfigs.length === 0) return;
    if (sessionsOfSelectedActivity.length === 0) return;
    if (!selectedActivity) return;

    // Only apply configs once per activity selection
    if (appliedConfigsForActivity === selectedActivity.activityId) return;

    // Apply positional configs to actual sessions by index - REPLACE, don't merge
    const newCustomizedSessions: Record<string, FlexibleSessionConfig> = {};
    positionalSessionConfigs.forEach((posConfig) => {
      const session = sessionsOfSelectedActivity[posConfig.index];
      if (session && (posConfig.customName || posConfig.startTime || posConfig.endTime)) {
        newCustomizedSessions[session.sessionId] = {
          sessionId: session.sessionId,
          customName: posConfig.customName || '',
          startTime: posConfig.startTime || parseTimeToHHmm(session.startTime),
          endTime: posConfig.endTime || parseTimeToHHmm(session.endTime),
        };
      }
    });

    setCustomizedSessions(newCustomizedSessions);
    setAppliedConfigsForActivity(selectedActivity.activityId);
  }, [sessionsOfSelectedActivity, positionalSessionConfigs, selectedActivity, appliedConfigsForActivity]);

  // Helper to save a customized session
  const saveCustomizedSession = (sessionId: string, config: FlexibleSessionConfig) => {
    setCustomizedSessions((prev) => ({ ...prev, [sessionId]: config }));
    setEditingSessionId(null);
  };

  // Helper to remove customization from a session
  const removeCustomization = (sessionId: string) => {
    setCustomizedSessions((prev) => {
      const updated = { ...prev };
      delete updated[sessionId];
      return updated;
    });
    setEditingSessionId(null);
  };

  const parseTimeToHHmm = (time: any): string => {
    if (!time) return '';
    if (typeof time === 'string') {
      const hhmmMatch = time.match(/^(\d{2}):(\d{2})/);
      if (hhmmMatch) return `${hhmmMatch[1]}:${hhmmMatch[2]}`;
      const dt = DateTime.fromISO(time);
      if (dt.isValid) return dt.toFormat('HH:mm');
    }
    if (time instanceof Date) {
      return DateTime.fromJSDate(time).toFormat('HH:mm');
    }
    return '';
  };

  // Get session config (customized or default from activity)
  const getSessionConfig = (session: CreateRosterTemplateScreenActivitySession) => {
    const customized = customizedSessions[session.sessionId];
    if (customized) {
      return {
        name: customized.customName || session.name,
        startTime: customized.startTime,
        endTime: customized.endTime,
        isCustomized: true,
      };
    }
    return {
      name: session.name,
      startTime: parseTimeToHHmm(session.startTime),
      endTime: parseTimeToHHmm(session.endTime),
      isCustomized: false,
    };
  };

  // Handle submission with flexible sessions
  const handleSubmitWithFlexibleSessions = async () => {
    if (!formik.values.rosterDates || formik.values.rosterDates.length === 0) {
      return;
    }

    const rosterDates = formik.values.rosterDates.map((date) => encodeDate(DateTime.fromJSDate(date)));

    try {
      // 1. Save the roster template
      const res = await saveRosterTemplateApi.run({
        rosterTemplateId: formik.values.rosterTemplateId,
        activityId: formik.values.activityId,
        rosterName: formik.values.rosterName,
        rosterNotes: formik.values.rosterNotes,
        rosterDates,
        location: formik.values.location.description ? formik.values.location : null,
        remoteLocation: formik.values.remoteLocation ?? null,
      });

      if (!res.ok) {
        setSnackbar({ open: true, message: 'Unable to save roster', variant: 'error' });
        return;
      }

      const createdRosterTemplateIds = res.data?.data?.rosterTemplateIds ?? [];
      const skippedCount = res.data?.data?.skippedDates?.length ?? 0;

      // 2. Create flexible sessions for each customized session and each roster date
      // Only include sessions that have valid start and end times
      const validCustomizedSessions = Object.entries(customizedSessions).filter(
        ([, config]) => config.startTime && config.endTime
      );

      if (validCustomizedSessions.length > 0) {
        const flexibleSessionPromises = validCustomizedSessions.flatMap(([sessionId, config]) => {
          const normalizedStart = parseTimeToHHmm(config.startTime);
          const normalizedEnd = parseTimeToHHmm(config.endTime);
          if (!normalizedStart || !normalizedEnd) return [];

          const startTime = DateTime.fromFormat(normalizedStart, 'HH:mm');
          const endTime = DateTime.fromFormat(normalizedEnd, 'HH:mm');
          if (!startTime.isValid || !endTime.isValid) return [];

          return rosterDates.map((rosterDate) =>
            addFlexibleSession.run({
              sessionId,
              sessionDate: rosterDate,
              startTime: encodeTime(startTime),
              endTime: encodeTime(endTime),
              name: config.customName || null,
            })
          );
        });

        // Wait for all flexible sessions to be created
        await Promise.all(flexibleSessionPromises);
      }

      // 3. Show success message
      const createdCount = createdRosterTemplateIds.length;
      let message = createdCount === 1 ? 'Roster saved' : `${createdCount} rosters saved`;
      if (skippedCount > 0) {
        message += ` (${skippedCount} skipped)`;
      }
      if (validCustomizedSessions.length > 0) {
        message += ` with ${validCustomizedSessions.length} customized session(s)`;
      }
      setSnackbar({ open: true, message, variant: 'success' });

      // 4. Call onCreated callback
      if (onCreated) {
        onCreated({ rosterTemplateIds: createdRosterTemplateIds, activityId: formik.values.activityId });
      }

      // 5. Call onSuccess
      if (onSuccess) {
        onSuccess(formik.values);
      }
    } catch (error) {
      console.error('Error creating roster template with flexible sessions:', error);
      setSnackbar({ open: true, message: 'Unable to save roster', variant: 'error' });
    }
  };

  const [
    runCheckRecurrenceDate,
    { data: checkRecurrenceDateData, loading: checkRecurrenceDateIsLoading },
  ] = useCampfireLazyQuery<RosterTemplateScreenCheckRecurrenceDate, RosterTemplateScreenCheckRecurrenceDateVariables>(
    ROSTER_TEMPLATE_SCREEN_CHECK_RECURRENCE_DATE
  );

  const [runGetActivityOccurrences, { data: activityOccurrencesData }] = useCampfireLazyQuery<
    RosterTemplateScreenGetActivityOccurrences,
    RosterTemplateScreenGetActivityOccurrencesVariables
  >(ROSTER_TEMPLATE_SCREEN_GET_ACTIVITY_OCCURRENCES);

  // Fetch occurrences when activity is selected (for bulk mode calendar)
  useEffect(() => {
    if (!selectedActivityId) return;
    const from = DateTime.local().toFormat('yyyy-MM-dd');
    const until = DateTime.local()
      .plus({ months: 6 })
      .toFormat('yyyy-MM-dd');
    runGetActivityOccurrences({
      variables: {
        activityId: selectedActivityId,
        from,
        until,
      },
    });
  }, [selectedActivityId]);

  const scheduledOccurrences = useMemo(() => {
    return activityOccurrencesData?.vm?.activity?.occurrencesBetween ?? [];
  }, [activityOccurrencesData]);

  const currentDate = formik.values.rosterDates[0];

  useEffect(() => {
    if (!selectedActivityId || !currentDate || dateMode !== 'single') return;
    const dateStr = encodeDate(DateTime.fromJSDate(currentDate));
    queriedDateRef.current = dateStr;
    runCheckRecurrenceDate({
      variables: {
        activityId: selectedActivityId,
        rosterDate: dateStr,
      },
    });
  }, [selectedActivityId, currentDate, dateMode]);

  useEffect(() => {
    if (!checkRecurrenceDateIsLoading && dateMode === 'single' && currentDate) {
      const currentDateStr = encodeDate(DateTime.fromJSDate(currentDate));
      if (queriedDateRef.current !== currentDateStr) return;
      const activity = checkRecurrenceDateData?.vm?.activity;

      const rosterTemplateExistsForDate = selectedActivity?.rosterTemplates?.find(
        (rt) => encodeDate(DateTime.fromISO(rt.rosterDate)) === currentDateStr
      );

      const initRosterDate = initRosterDates?.[0];
      const isEditingRosterInitialDate =
        initRosterDate && encodeDate(DateTime.fromJSDate(initRosterDate)) === currentDateStr;

      // Only show dialog if date changed and is not the one we're editing
      const prevDateStr = prevDate ? encodeDate(DateTime.fromJSDate(prevDate)) : null;
      const dateActuallyChanged = currentDateStr !== prevDateStr;

      if (activity?.occursOn && !isEditingRosterInitialDate && dateActuallyChanged) {
        setPrevDate(currentDate);
        setShowRosterExistDialog(true);
        return;
      }

      if (rosterTemplateExistsForDate && !isEditingRosterInitialDate && dateActuallyChanged) {
        setPrevDate(currentDate);
        setShowRosterExistDialog(true);
        return;
      }

      if (publishedRoster && encodeDate(DateTime.fromISO(publishedRoster.activityDate)) !== currentDateStr) {
        setShowRosterCancellationDialog(true);
        return;
      }

      // Update prevDate for valid dates
      if (dateActuallyChanged && !activity?.occursOn && !rosterTemplateExistsForDate) {
        setPrevDate(currentDate);
      }
    }
  }, [
    checkRecurrenceDateData,
    publishedRoster,
    prevDate,
    checkRecurrenceDateIsLoading,
    dateMode,
    currentDate,
    selectedActivity,
    initRosterDates,
  ]);

  useEffect(() => {
    if (selectedActivity) {
      setSelectedProgramId(selectedActivity.program.programId);
      const selectedActivityLocation = selectedActivity.activityLocation;

      if (!isEditMode) {
        formik.setFieldValue('remoteLocation', {
          activityRemoteLocationId: selectedActivity.activityRemoteLocation?.activityRemoteLocationId,
          details: selectedActivity.activityRemoteLocation?.details,
        });

        // Only set location from activity if there isn't already a prefilled location
        const currentLocation = formik.values.location;
        const hasPrefilledLocation =
          (currentLocation?.description && currentLocation.description.trim() !== '') ||
          (currentLocation?.formatted && currentLocation.formatted.trim() !== '');
        if (!hasPrefilledLocation) {
          formik.setFieldValue('location', {
            activityLocationId: selectedActivityLocation?.activityLocationId ?? '',
            description: selectedActivityLocation?.placesAddress.description ?? '',
            formatted: selectedActivityLocation?.placesAddress.formatted ?? '',
            latitude: selectedActivityLocation?.placesAddress.latLong.lat ?? 0,
            longitude: selectedActivityLocation?.placesAddress.latLong.long ?? 0,
            placesId: selectedActivityLocation?.placesAddress.placesId ?? '',
          });
        }
      }
    }
  }, [selectedActivity, isEditMode]);

  useEffect(() => {
    if (selectedActivityId) {
      formik.setFieldValue('activityId', selectedActivityId);
    }
  }, [selectedActivityId]);

  useEffect(() => {
    if (selectedProgramId) {
      formik.setFieldValue('programId', selectedProgramId);
    }
  }, [selectedProgramId]);

  const activitiesOfSelectedProgram = useMemo(() => {
    return activities?.filter((activity) => activity.program.programId === selectedProgramId);
  }, [activities, selectedProgramId]);

  useEffect(() => {
    // Don't fall back to the first activity while the activity list is still loading,
    // otherwise a prefilled activity gets overwritten before it can be matched
    if (loading || activities.length === 0) return;
    const isProgramUpdated = !activitiesOfSelectedProgram.find(
      (activity) => activity.activityId === selectedActivityId
    );
    if (isProgramUpdated) {
      const firstActivity = arrayHead(activitiesOfSelectedProgram);
      if (firstActivity) setSelectedActivityId(firstActivity.activityId);
    }
  }, [activitiesOfSelectedProgram, selectedActivityId, loading, activities]);

  // TODO: handle error
  const fieldsUnchanged: boolean = useMemo(() => {
    const datesUnchanged =
      formik.values.rosterDates.length === formik.initialValues.rosterDates.length &&
      formik.values.rosterDates.every((d, i) => d?.getTime() === formik.initialValues.rosterDates[i]?.getTime());
    return (
      formik.values.rosterNotes === formik.initialValues.rosterNotes &&
      datesUnchanged &&
      formik.values.location === formik.initialValues.location
    );
  }, [formik.values.rosterNotes, formik.values.rosterDates, formik.values.location]);

  const hasLocationChanged: boolean = useMemo(() => {
    return formik.values.location !== formik.initialValues.location;
  }, [formik.values.location]);

  const validateRosterDate = useCallback(
    (rosterDate: Date | undefined) => {
      if (!rosterDate || !selectedActivity?.startDate) {
        return undefined;
      }
      const isoDate = DateTime.fromJSDate(rosterDate).toISODate();

      if (isoDate && isoDate < selectedActivity.startDate) {
        return "Roster cannot be earlier than the activity's start date";
      }

      if (checkRecurrenceDateData?.vm?.activity?.occursOn) {
        return 'Roster already exists for this date';
      }
      return undefined;
    },
    [selectedActivity?.startDate, checkRecurrenceDateData]
  );

  const handleDateModeChange = (newMode: DateMode | null) => {
    if (newMode !== null) {
      setDateMode(newMode);
      // Reset dates when switching modes
      formik.setFieldValue('rosterDates', newMode === 'range' ? [] : [new Date()]);
      setRangeStartDate(null);
      setRangeEndDate(null);
    }
  };

  const handleBulkDatesChange = (dates: DateObject[]) => {
    const jsDateArray = dates.map((d) => d.toDate());
    formik.setFieldValue('rosterDates', jsDateArray);
  };

  const generateDatesInRange = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const startDt = DateTime.fromJSDate(start).startOf('day');
    const endDt = DateTime.fromJSDate(end).startOf('day');

    for (let cursor = startDt; cursor <= endDt; cursor = cursor.plus({ days: 1 })) {
      const dateStr = cursor.toFormat('yyyy-MM-dd');
      const isScheduledOccurrence = scheduledOccurrences.includes(dateStr);
      const existingRoster = selectedActivity?.rosterTemplates?.find(
        (rt) => DateTime.fromISO(rt.rosterDate).toFormat('yyyy-MM-dd') === dateStr
      );

      if (!isScheduledOccurrence && !existingRoster) {
        dates.push(cursor.toJSDate());
      }
    }
    return dates;
  };

  const updateRangeRosterDates = (start: Date | null, end: Date | null) => {
    if (start && end && start <= end) {
      const datesInRange = generateDatesInRange(start, end);
      formik.setFieldValue('rosterDates', datesInRange);
    } else {
      formik.setFieldValue('rosterDates', []);
    }
  };

  const handleRangeStartChange = (date: Date | undefined) => {
    const newStart = date ?? null;
    setRangeStartDate(newStart);
    updateRangeRosterDates(newStart, rangeEndDate);
  };

  const handleRangeEndChange = (date: Date | undefined) => {
    const newEnd = date ?? null;
    setRangeEndDate(newEnd);
    updateRangeRosterDates(rangeStartDate, newEnd);
  };

  return (
    <Box className={classes.container}>
      <LinearProgressOverlay isLoading={loading || checkRecurrenceDateIsLoading} />
      <Typography variant='h6' style={{ fontWeight: 600, paddingBottom: 16 }}>
        {formik.values.rosterTemplateId ? 'Edit Roster' : 'Add New Roster'}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            slow
            fullWidth
            includeEmpty
            variant='outlined'
            name='programId'
            label='Program'
            items={programs}
            disabled={!!formik.values.rosterTemplateId}
            onChange={(event) => setSelectedProgramId(event.target.value)}
            renderFunction={(program: CreateRosterTemplateScreenProgram) => program.name}
            valueFunction={(program: CreateRosterTemplateScreenProgram) => program.programId}
          />
        </Grid>
        <Grid item xs={12}>
          {activitiesOfSelectedProgram && (
            <Select
              slow
              fullWidth
              includeEmpty
              variant='outlined'
              name='activityId'
              label='Activity'
              items={activitiesOfSelectedProgram}
              disabled={!!formik.values.rosterTemplateId}
              onChange={(event) => setSelectedActivityId(event.target.value)}
              renderFunction={(activity: CreateRosterTemplateScreenActivity) => activity.name}
              valueFunction={(activity: CreateRosterTemplateScreenActivity) => activity.activityId}
            />
          )}
        </Grid>

        {selectedActivity?.locationType === 'local' ? (
          <Grid item xs={12}>
            <AutoLocationField name='location' label='Location' helper fullWidth variant='outlined' required />
          </Grid>
        ) : null}

        {selectedActivity?.locationType === 'remote' ? (
          <Grid item xs={12}>
            <Field
              name='remoteLocation.details'
              label='Location - Add further information (link, phone number, etc)'
              variant='outlined'
              helper
              fullWidth
              autoFocus
            />
          </Grid>
        ) : null}

        {!isEditMode && (
          <Grid item xs={12}>
            <Box display='flex' alignItems='center' mb={2} style={{ gap: '12px' }}>
              <Typography variant='body2' style={{ fontWeight: 600 }}>
                Dates
              </Typography>
              <ToggleButtonGroup
                value={dateMode}
                exclusive
                onChange={(_unusedEvent, newMode) => handleDateModeChange(newMode)}
              >
                <ToggleButton
                  value='single'
                  style={
                    dateMode === 'single'
                      ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                      : { padding: '6px 10px' }
                  }
                >
                  <Typography style={{ fontWeight: 600, fontSize: '11px' }}>Single</Typography>
                </ToggleButton>
                <ToggleButton
                  value='bulk'
                  style={
                    dateMode === 'bulk'
                      ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                      : { padding: '6px 10px' }
                  }
                >
                  <Typography style={{ fontWeight: 600, fontSize: '11px' }}>Bulk</Typography>
                </ToggleButton>
                <ToggleButton
                  value='range'
                  style={
                    dateMode === 'range'
                      ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                      : { padding: '6px 10px' }
                  }
                >
                  <Typography style={{ fontWeight: 600, fontSize: '11px' }}>Range</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          {dateMode === 'single' && (
            <DatePickerField
              name='rosterDates[0]'
              label='Date'
              autoOk
              fullWidth
              required
              inputVariant='outlined'
              InputLabelProps={{ shrink: true }}
              minDate={selectedActivity ? selectedActivity.startDate : undefined}
              validate={validateRosterDate}
            />
          )}
          {dateMode === 'bulk' && (
            <Box>
              <Calendar
                multiple
                format='DD MMM YY'
                minDate={DateTime.local().toFormat('yyyy-MM-dd')}
                mapDays={({ date }) => {
                  const dateStr = date.format('YYYY-MM-DD');

                  // Check if date is a scheduled recurring occurrence
                  const isScheduledOccurrence = scheduledOccurrences.includes(dateStr);

                  // Check if roster template already exists for this date
                  const existingRoster = selectedActivity?.rosterTemplates?.find(
                    (rt) => DateTime.fromISO(rt.rosterDate).toFormat('yyyy-MM-dd') === dateStr
                  );

                  if (isScheduledOccurrence || existingRoster) {
                    return {
                      disabled: true,
                      style: {
                        backgroundColor: '#D7DDE2',
                        color: '#8899AC',
                      },
                    };
                  }

                  return {};
                }}
                value={formik.values.rosterDates.map((d) => new DateObject(d))}
                onChange={handleBulkDatesChange}
                className='multi-date-picker'
                plugins={[<DatePanel position='right' sort='date' />]}
              />
              <Box display='flex' justifyContent='space-between' alignItems='center' style={{ marginTop: 8 }}>
                <Box display='flex' alignItems='center'>
                  <Box
                    style={{ width: 16, height: 16, backgroundColor: '#D7DDE2', borderRadius: '50%', marginRight: 6 }}
                  />
                  <Typography variant='caption' color='textSecondary'>
                    Existing roster
                  </Typography>
                </Box>
                <Typography variant='caption' style={{ fontWeight: 700 }}>
                  {formik.values.rosterDates.length} selected
                </Typography>
              </Box>
            </Box>
          )}
          {dateMode === 'range' && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <KeyboardDatePicker
                    fullWidth
                    variant='inline'
                    format='dd/MM/yyyy'
                    id='range-start-date'
                    value={rangeStartDate}
                    label='Start Date'
                    inputVariant='outlined'
                    onChange={(date) => handleRangeStartChange(date?.toJSDate())}
                    autoOk
                    placeholder='dd/mm/yyyy'
                    allowKeyboardControl
                    InputAdornmentProps={{ position: 'start' }}
                    KeyboardButtonProps={{ tabIndex: -1 }}
                    minDate={selectedActivity?.startDate}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <KeyboardDatePicker
                    fullWidth
                    variant='inline'
                    format='dd/MM/yyyy'
                    id='range-end-date'
                    value={rangeEndDate}
                    label='End Date'
                    inputVariant='outlined'
                    onChange={(date) => handleRangeEndChange(date?.toJSDate())}
                    autoOk
                    placeholder='dd/mm/yyyy'
                    allowKeyboardControl
                    InputAdornmentProps={{ position: 'start' }}
                    KeyboardButtonProps={{ tabIndex: -1 }}
                    minDate={rangeStartDate ?? selectedActivity?.startDate}
                  />
                </Grid>
              </Grid>
              <Box display='flex' justifyContent='space-between' alignItems='center' style={{ marginTop: 8 }}>
                <Typography variant='caption' color='textSecondary'>
                  Existing rosters will be skipped
                </Typography>
                <Typography variant='caption' style={{ fontWeight: 700 }}>
                  {formik.values.rosterDates.length} dates to create
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>

        {/* <Grid item xs={12}>
          <Collapse in={false}>
            <RichEditorFormik valueName='rosterNotes' placeholder='Notes (Optional)' />
          </Collapse>

          {!showNoteSection ? (
            <HoverText variant='body2' color='primary' onClick={() => setShowNoteSection(true)}>
              {'Add notes for this roster'}
            </HoverText>
          ) : null}
        </Grid> */}
        <Grid item xs={12}>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Typography variant='h6' style={{ fontWeight: 600 }}>
              Sessions
            </Typography>
            {enableFlexibleSessions && Object.keys(customizedSessions).length > 0 && (
              <Typography variant='caption' style={{ color: theme.palette.primary.main }}>
                {Object.keys(customizedSessions).length} customized
              </Typography>
            )}
          </Box>
          {enableFlexibleSessions && (
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
              Click &quot;Customize&quot; to modify session times for this roster.
            </Typography>
          )}
          {sessionsOfSelectedActivity.length > 0
            ? sessionsOfSelectedActivity?.map((session: CreateRosterTemplateScreenActivitySession) => {
                const { activityLocation: sessionLocation } = session;
                const sessionConfig = getSessionConfig(session);
                const isEditing = editingSessionId === session.sessionId;

                return (
                  <Box
                    key={session.sessionId}
                    borderBottom={`1px solid ${theme.color.grey.border}`}
                    px={2}
                    pb={2}
                    pt={1}
                  >
                    <Box display='flex' alignItems='center' justifyContent='space-between'>
                      <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                        <Typography variant='h6'>{sessionConfig.name}</Typography>
                        {sessionConfig.isCustomized && (
                          <Typography
                            variant='caption'
                            style={{
                              backgroundColor: theme.palette.primary.light,
                              color: theme.palette.primary.main,
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontWeight: 600,
                            }}
                          >
                            Customized
                          </Typography>
                        )}
                      </Box>
                      {enableFlexibleSessions && !isEditing && (
                        <TabletButton
                          variant='text'
                          size='small'
                          startIcon={<Edit style={{ fontSize: 16 }} />}
                          onClick={() => setEditingSessionId(session.sessionId)}
                          style={{ textTransform: 'none' }}
                        >
                          Customize
                        </TabletButton>
                      )}
                    </Box>

                    <Collapse in={isEditing}>
                      <Box mt={2} p={2} bgcolor={theme.color.grey.background} borderRadius={4}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size='small'
                              variant='outlined'
                              label='Custom Name (optional)'
                              placeholder={session.name}
                              value={customizedSessions[session.sessionId]?.customName || ''}
                              onChange={(e) => {
                                const current = customizedSessions[session.sessionId] || {
                                  sessionId: session.sessionId,
                                  customName: '',
                                  startTime: session.startTime || '',
                                  endTime: session.endTime || '',
                                };
                                setCustomizedSessions((prev) => ({
                                  ...prev,
                                  [session.sessionId]: { ...current, customName: e.target.value },
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size='small'
                              variant='outlined'
                              label='Start Time'
                              type='time'
                              InputLabelProps={{ shrink: true }}
                              value={
                                customizedSessions[session.sessionId]?.startTime ?? parseTimeToHHmm(session.startTime)
                              }
                              onChange={(e) => {
                                const current = customizedSessions[session.sessionId] || {
                                  sessionId: session.sessionId,
                                  customName: '',
                                  startTime: parseTimeToHHmm(session.startTime),
                                  endTime: parseTimeToHHmm(session.endTime),
                                };
                                setCustomizedSessions((prev) => ({
                                  ...prev,
                                  [session.sessionId]: { ...current, startTime: e.target.value },
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size='small'
                              variant='outlined'
                              label='End Time'
                              type='time'
                              InputLabelProps={{ shrink: true }}
                              value={customizedSessions[session.sessionId]?.endTime ?? parseTimeToHHmm(session.endTime)}
                              onChange={(e) => {
                                const current = customizedSessions[session.sessionId] || {
                                  sessionId: session.sessionId,
                                  customName: '',
                                  startTime: parseTimeToHHmm(session.startTime),
                                  endTime: parseTimeToHHmm(session.endTime),
                                };
                                setCustomizedSessions((prev) => ({
                                  ...prev,
                                  [session.sessionId]: { ...current, endTime: e.target.value },
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box display='flex' justifyContent='space-between'>
                              <Box>
                                {customizedSessions[session.sessionId] && (
                                  <TabletButton
                                    variant='text'
                                    size='small'
                                    onClick={() => removeCustomization(session.sessionId)}
                                    style={{ textTransform: 'none', color: theme.palette.error.main }}
                                  >
                                    Reset to Default
                                  </TabletButton>
                                )}
                              </Box>
                              <Box>
                                <TabletButton
                                  variant='text'
                                  size='small'
                                  onClick={() => setEditingSessionId(null)}
                                  style={{ textTransform: 'none', marginRight: 8 }}
                                >
                                  Cancel
                                </TabletButton>
                                <TabletButton
                                  variant='contained'
                                  color='primary'
                                  size='small'
                                  onClick={() => {
                                    const current = customizedSessions[session.sessionId];
                                    if (current) {
                                      saveCustomizedSession(session.sessionId, current);
                                    } else {
                                      setEditingSessionId(null);
                                    }
                                  }}
                                  style={{ textTransform: 'none' }}
                                >
                                  Save
                                </TabletButton>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>

                    {!isEditing && (
                      <>
                        <Typography variant='body2' color='textSecondary'>
                          {getDisplayTimeSchedule(sessionConfig.startTime, sessionConfig.endTime)}
                        </Typography>
                        <Box display='flex' alignItems='center'>
                          <Typography variant='subtitle2' color='textSecondary' className={classes.textGap}>
                            {`Report`}
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            {session.reportType === null
                              ? session.autoReport
                                ? 'No Report Required (Impact Tracked)'
                                : 'No Report Required (No Impact)'
                              : session.reportType?.name}
                          </Typography>
                        </Box>
                        {sessionLocation && (
                          <Box display='flex'>
                            <Room />
                            <Typography variant='body2' color='textSecondary'>
                              {sessionLocation.placesAddress.formatted}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                );
              })
            : null}
        </Grid>
        <Grid item xs={12}>
          <TabletButton
            variant='outlined'
            color='primary'
            onClick={() => setShowNoteSection(true)}
            style={{ textTransform: 'none' }}
          >
            {formik.values.rosterNotes ? 'Edit Roster Notes' : 'Add Roster Notes'}
          </TabletButton>
        </Grid>
        <Grid container justify='space-between' alignItems='center' style={{ padding: 16 }}>
          <Grid item xs={4}>
            <TabletButton
              variant='text'
              color='error'
              onClick={() => setShowDeleteConfirmationDialog(true)}
              disabled={!formik.values.rosterTemplateId}
            >
              {'Delete'}
            </TabletButton>
          </Grid>
          <Grid item container xs={8} justify='flex-end'>
            <Grid item>
              <TabletButton
                onClick={() => {
                  if (onSuccess) {
                    onSuccess();
                  } else {
                    history.push(`/management/rosters?activityId=${selectedActivityId}${viewParam}`);
                  }
                }}
              >
                {'Cancel'}
              </TabletButton>
            </Grid>
            <Grid item>
              {isEditMode ? (
                <TabletButton
                  color='primary'
                  variant='contained'
                  disabled={fieldsUnchanged}
                  onClick={() =>
                    hasLocationChanged && publishedRoster
                      ? setShowPromptDialog(true)
                      : runSaveRosterTemplate({ values: formik.values, onSuccess })
                  }
                >
                  {'Save Changes'}
                </TabletButton>
              ) : (
                <TabletButton
                  color='primary'
                  variant='contained'
                  type={enableFlexibleSessions ? 'button' : 'submit'}
                  disabled={isSubmitting || saveRosterTemplateApi.isLoading}
                  onClick={enableFlexibleSessions ? handleSubmitWithFlexibleSessions : undefined}
                >
                  {'Create'}
                </TabletButton>
              )}
            </Grid>
          </Grid>
        </Grid>
        {showRosterExistDialog && currentDate && (
          <ConfirmationDialog
            title={'This roster already exists'}
            body={`A roster with these settings already exist, do you want to view the roster ?`}
            open
            closeActionText={'View Roster'}
            confirmActionText={'Continue'}
            closeActionColor={'default'}
            confirmActionColor={'primary'}
            handleCloseClick={() => {
              if (onSuccess) {
                onSuccess();
              } else {
                history.push(
                  `/management/rosters?activityDate=${encodeDate(
                    DateTime.fromJSDate(currentDate)
                  )}&activityId=${selectedActivityId}${viewParam}`
                );
              }
            }}
            handleConfirmClick={() => {
              setShowRosterExistDialog(false);
            }}
          />
        )}
        {prevDate && selectedActivityId && (
          <RosterCancellationDialog
            open={showRosterCancellationDialog}
            handleClose={() => {
              setShowRosterCancellationDialog(false);
              formik.setFieldValue('rosterDates', [prevDate]);
            }}
            activityId={selectedActivityId}
            activityDate={encodeDate(DateTime.fromJSDate(prevDate))}
            onSuccess={onSuccess}
          />
        )}
        {showDeleteConfirmationDialog && (
          <ConfirmationDialog
            title={'Delete this roster?'}
            body={`Deleting this roster will remove it and any draft or published rosters you have created. Additionally, any one-off or flexible sessions will be removed.
              
              This action cannot be undone.`}
            open
            closeActionText={'Cancel'}
            confirmActionText={'Delete Roster'}
            handleCloseClick={() => {
              setShowDeleteConfirmationDialog(false);
            }}
            handleConfirmClick={() => {
              runDeleteRosterTemplate({ rosterTemplateId: formik.values.rosterTemplateId as string, onDeleted });
            }}
          />
        )}
        {showPromptDialog && (
          <ConfirmationDialog
            title={'Update location for this roster ?'}
            body={`The location for this roster will be updated, and rostered volunteers will be notified of this change.`}
            open
            closeActionText={'Cancel'}
            confirmActionText={'Save Changes'}
            handleCloseClick={() => {
              setShowPromptDialog(false);
              formik.setFieldValue('location', prevLocation);
            }}
            handleConfirmClick={() => {
              runSaveRosterTemplate({ values: formik.values, onSuccess });
            }}
          />
        )}
        {showNoteSection && (
          <RosterNotesDialog
            open={showNoteSection}
            rosterNotes={formik.values.rosterNotes || null}
            rosterName={formik.values.rosterName || null}
            edit
            onCancel={() => setShowNoteSection(false)}
            onSave={(notes, name) => {
              formik.setFieldValue('rosterNotes', notes);
              formik.setFieldValue('rosterName', name ?? null);
              setShowNoteSection(false);
            }}
            title='Roster Notes'
            subject='roster'
          />
        )}
      </Grid>
    </Box>
  );
};
type PublishedRoster = {
  rosterId: string;
  activityDate: string;
  rosterTemplate: {
    rosterTemplateId: string;
  } | null;
};
type DraftRoster = {
  draftRosterId: string;
};
export const RosterTemplateForm = ({
  predefinedFormValues,
  publishedRoster,
  onSuccess,
  onDeleted,
  inDialog,
  enableFlexibleSessions,
  onCreated,
}: {
  predefinedFormValues?: Partial<RosterTemplateFormValues>;
  publishedRoster?: PublishedRoster | null;
  onSuccess?: (values?: RosterTemplateFormValues) => void;
  onDeleted?: () => void;
  inDialog?: boolean;
  enableFlexibleSessions?: boolean;
  onCreated?: (data: { rosterTemplateIds: string[]; activityId: string }) => void;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { runSaveRosterTemplate } = useRosterTemplateActions();

  const {
    getVolunteerIdentity,
    maybeManagerIdentity,
    user: { userIdentityService },
  } = useUser();

  const managedProgramIds = maybeManagerIdentity
    ? maybeManagerIdentity.programManagerIdentities?.map((pmi) => pmi.programId)
    : [];

  const activityIds = getVolunteerIdentity()?.programIdentities.flatMap((pi) =>
    managedProgramIds.find((id) => pi.programId === id) ? pi.activityIds : pi.ledActivityIds
  );

  const { data: allData, loading: allLoading } = useCampfireQuery<
    CreateRosterTemplateScreenGetProgramsAndActivities,
    undefined
  >(CREATE_ROSTER_TEMPLATE_SCREEN_GET_PROGRAMS_AND_ACTIVITIES);

  const { data: subsetData, loading: subsetLoading } = useCampfireQuery<
    CreateRosterTemplateScreenGetActivities,
    CreateRosterTemplateScreenGetActivitiesVariables
  >(CREATE_ROSTER_TEMPLATE_SCREEN_GET_ACTIVITIES, {
    options: {
      variables: {
        activityIds,
      },
    },
  });

  const activities = useMemo(() => {
    if (userIdentityService.isVmAdmin || userIdentityService.isProgramManager) {
      return allData?.vm.activities ?? [];
    }
    return subsetData?.vm.activities ?? [];
  }, [allData, subsetData]);

  const programs = useMemo(() => {
    if (userIdentityService.isVmAdmin || userIdentityService.isProgramManager) {
      return allData?.vm.programs ?? [];
    }
    return subsetData?.vm.activities?.map((activity) => activity.program) ?? [];
  }, [allData, subsetData]);

  const initialValues = useMemo(
    () => ({
      rosterTemplateId: predefinedFormValues?.rosterTemplateId ?? undefined,
      programId: predefinedFormValues?.programId ?? '',
      activityId: predefinedFormValues?.activityId ?? '',
      rosterDates: predefinedFormValues?.rosterDates ?? [new Date()],
      rosterName: predefinedFormValues?.rosterName ?? undefined,
      rosterNotes: predefinedFormValues?.rosterNotes ?? undefined,
      location: {
        description: predefinedFormValues?.location?.description ?? '',
        formatted: predefinedFormValues?.location?.formatted ?? '',
        latitude: predefinedFormValues?.location?.latitude ?? 0,
        longitude: predefinedFormValues?.location?.longitude ?? 0,
        placesId: predefinedFormValues?.location?.placesId ?? '',
      },
      remoteLocation: predefinedFormValues?.remoteLocation ?? {
        activityRemoteLocationId: '',
        details: '',
      },
    }),
    [predefinedFormValues]
  );
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setIsSubmitting(true);
        runSaveRosterTemplate({ values, onSuccess }).finally(() => {
          setIsSubmitting(false);
        });
      }}
    >
      {() => (
        <PaneWrapper>
          <Pane>
            <Form style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflowY: 'hidden' }}>
              <CreateRosterTemplateForm
                activities={activities}
                programs={programs}
                loading={allLoading || subsetLoading}
                isSubmitting={isSubmitting}
                publishedRoster={publishedRoster}
                initRosterDates={predefinedFormValues?.rosterDates}
                onSuccess={onSuccess}
                onDeleted={onDeleted}
                inDialog={inDialog}
                enableFlexibleSessions={enableFlexibleSessions}
                onCreated={onCreated}
                initialFlexibleSessionConfigs={predefinedFormValues?.flexibleSessionConfigs}
                positionalSessionConfigs={predefinedFormValues?.positionalSessionConfigs}
              />
            </Form>
          </Pane>
        </PaneWrapper>
      )}
    </Formik>
  );
};
